import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Tag } from '@/components/badges';
import { NAV_SECTIONS, SECTION_LABEL, sectionAnchor, type ProjectSettingsSection } from './settingsSections';

interface Props {
  /** The section being looked at (the URL's, `general` by default). */
  section: ProjectSettingsSection;
  counts: Partial<Record<ProjectSettingsSection, string>>;
  onGo: (section: ProjectSettingsSection) => void;
}

const LINK_CLASS = [
  'relative flex h-9 items-center gap-2 whitespace-nowrap rounded-lg border px-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground max-sm:h-11',
  'xl:border-transparent xl:before:absolute xl:before:-left-px xl:before:bottom-1.5 xl:before:top-1.5 xl:before:w-0.5 xl:before:rounded-full xl:before:bg-primary xl:before:opacity-0',
  'aria-[current=location]:border-border-strong aria-[current=location]:bg-secondary aria-[current=location]:text-foreground xl:aria-[current=location]:before:opacity-100',
];

/** Section nav: a column from 1280 px, one scrolling row below. */
export function SettingsNav({ section, counts, onGo }: Props) {
  return (
    <nav aria-label="Settings sections" className="min-w-0 xl:sticky xl:top-24">
      <Kicker className="mb-3 hidden px-3 text-subtle xl:flex">Sections</Kicker>
      <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 xl:flex-col xl:gap-0.5 xl:overflow-visible xl:pb-0">
        {NAV_SECTIONS.map((s) => (
          <li key={s} className={cn('shrink-0', s === 'danger' && 'xl:mt-2 xl:border-t xl:pt-2')}>
            <NavLink s={s} current={section === s} count={counts[s]} onGo={onGo} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

function NavLink({ s, current, count, onGo }: { s: ProjectSettingsSection; current: boolean; count: string | undefined; onGo: (section: ProjectSettingsSection) => void }) {
  return (
    <a
      href={`#${sectionAnchor(s)}`}
      data-testid={`settings-nav-${s}`}
      aria-current={current ? 'location' : undefined}
      onClick={(e) => {
        e.preventDefault();
        onGo(s);
        if (current) document.getElementById(sectionAnchor(s))?.scrollIntoView({ block: 'start' });
      }}
      className={cn(...LINK_CLASS, s === 'danger' && 'text-destructive hover:text-destructive aria-[current=location]:text-destructive')}
    >
      {s === 'danger' ? <Icon name="warning" size={16} /> : null}
      {SECTION_LABEL[s]}
      {count ? <Tag className="ml-auto tabular">{count}</Tag> : null}
    </a>
  );
}
